import { Link, useParams } from 'react-router-dom';
import { getArticleById, getLatestArticles } from '@/mocks/articles';
import { getVideoById, getVideosByCategory } from '@/mocks/videos';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);
  const article = getArticleById(articleId);

  const latestArticles = getLatestArticles(4).filter((a) => a.id !== articleId).slice(0, 3);

  if (!article) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-bold text-foreground-800 mb-2">文章未找到</h2>
          <p className="text-foreground-500 mb-4">该文章可能已被删除或链接失效</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.publishDate,
        image: article.coverImage,
        publisher: { '@type': 'Organization', name: '极速影视' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://chentaotao.cn' },
          { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://chentaotao.cn/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
    ],
  };

  function renderContent(content: string) {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={i} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        elements.push(
          <h3 key={i} className="font-heading text-lg font-bold text-foreground-800 mt-6 mb-3">
            {line.replace('### ', '')}
          </h3>
        );
      } else if (line.startsWith('#### ')) {
        elements.push(
          <h4 key={i} className="font-heading text-base font-bold text-foreground-700 mt-5 mb-2">
            {line.replace('#### ', '')}
          </h4>
        );
      } else if (line.startsWith('> ')) {
        elements.push(
          <blockquote key={i} className="border-l-4 border-primary-400 bg-primary-50 px-4 py-3 my-4 rounded-r-lg text-foreground-600 italic text-sm">
            {line.replace('> ', '')}
          </blockquote>
        );
      } else if (line.startsWith('- **')) {
        const match = line.match(/- \*\*(.+?)\*\*[：:]?\s*(.*)/);
        if (match) {
          elements.push(
            <li key={i} className="ml-4 mb-1 text-sm text-foreground-700">
              <strong>{match[1]}</strong>：{match[2]}
            </li>
          );
        }
      } else if (line.startsWith('- ')) {
        elements.push(
          <li key={i} className="ml-4 mb-1 text-sm text-foreground-700 list-disc">
            {line.replace('- ', '')}
          </li>
        );
      } else if (line.startsWith('1. ') || line.startsWith('2. ') || line.startsWith('3. ')) {
        const match = line.match(/^\d+\.\s+(.+)/);
        if (match) {
          elements.push(
            <li key={i} className="ml-4 mb-1 text-sm text-foreground-700 list-decimal">
              {match[1]}
            </li>
          );
        }
      } else if (line.trim() === '') {
        elements.push(<div key={i} className="h-3"></div>);
      } else {
        elements.push(
          <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
            {line}
          </p>
        );
      }
      i++;
    }

    return elements;
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <article className="pt-20 min-h-screen bg-background-50">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <header className="mt-6 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  {article.categoryName}
                </span>
                <span className="text-xs text-foreground-400">{article.publishDate}</span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.readCount.toLocaleString()} 阅读
                </span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 font-bold text-sm">
                  {article.author.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                  <p className="text-xs text-foreground-400">极速影视特约作者</p>
                </div>
              </div>
            </header>

            <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose-custom">
              {renderContent(article.content)}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-foreground-500">标签：</span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs cursor-pointer hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap">
                  {article.categoryName}
                </span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs cursor-pointer hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap">
                  极速影视
                </span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs cursor-pointer hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap">
                  在线观看
                </span>
              </div>
            </div>

            {latestArticles.length > 0 && (
              <section className="mt-14">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  相关推荐
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {latestArticles.map((articleItem) => (
                    <Link
                      key={articleItem.id}
                      to={`/article/${articleItem.id}`}
                      className="group bg-background-50 border border-background-200 rounded-xl overflow-hidden cursor-pointer transition-all hover:-translate-y-1"
                    >
                      <div className="relative w-full aspect-[16/10] overflow-hidden">
                        <img
                          src={articleItem.coverImage}
                          alt={articleItem.title}
                          title={articleItem.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="font-heading text-sm font-bold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                          {articleItem.title}
                        </h4>
                        <p className="text-xs text-foreground-400 mt-2">{articleItem.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </article>
    </>
  );
}